import IconChevronDown from '@apollo/icons/default/IconChevronDown.svg';
import IconChevronRight from '@apollo/icons/default/IconChevronRight.svg';
import {
  Button,
  ButtonGroup,
  Checkbox,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Spinner,
  Tooltip,
} from '@apollo/orbit';
import React, { useState } from 'react';

import { Form } from 'src/components/common/form/Form';
import { JavascriptCodeBlock } from 'src/components/javascriptCodeBlock/JavascriptCodeBlock';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

export interface AddEventsState {
  notificationType?:
    | 'SchemaPublishSubscription'
    | 'ScheduledSummary'
    | 'BuildSubscription'
    | 'ProposalLifecycleSubscription';
  events?: GraphQLTypes.ProposalLifecycleEvent[];
}

interface Props {
  isOpen: boolean;
  state: Readonly<AddEventsState> | null;
  onCheckboxChange: (events: GraphQLTypes.ProposalLifecycleEvent[]) => void;
  onComplete: () => void;
  onPrevious?: () => void;
  onClose: () => void;
  mode: AddEventsMode;
  loading?: boolean;
}

const proposalCreatedPayload = `
{
  "eventType": "APOLLO_PROPOSAL_CREATED",
  "eventID": uuid,
  "graphID": string,
  "variantID": string, // graphRef format
  "proposalID": uuid,
  "timestamp": string; // ISO 8601 Date string
}
`;
const revisionSavedPayload = `
{
  "eventType": "APOLLO_PROPOSAL_REVISION_SAVED",
  "eventID": uuid,
  "graphID": string,
  "variantID": string, // graphRef format
  "proposalID": uuid,
  "change": { "revisionId": uuid },
  "timestamp": string; // ISO 8601 Date string
}
`;
const statusChangePayload = `
{
  "eventType": "APOLLO_PROPOSAL_STATUS_CHANGE",
  "eventID": uuid,
  "graphID": string,
  "variantID": string, // graphRef format
  "proposalID": uuid,
  "change": {
       "status": ProposalStatus,
       "previousStatus": ProposalStatus
  },
  "timestamp": string; // ISO 8601 Date string
}
`;

type AddEventCheckboxProps = {
  testId: string;
  title: string;
  name: string;
  isChecked?: boolean;
  onChange: () => void;
  code: string;
};

const AddEventCheckbox = ({
  testId,
  title,
  name,
  isChecked,
  onChange,
  code,
}: AddEventCheckboxProps) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="-mt-1 grow text-base">
      <Checkbox
        data-testid={testId}
        title={title}
        name={name}
        onChange={onChange}
        isChecked={isChecked}
        className="mr-3 shrink-0"
      >
        <div className="font-semibold text-neutral">{name}</div>
      </Checkbox>
      <div className="flex justify-start">
        <div className="mr-1 text-placeholder">{title}</div>
        <Tooltip
          placement="top"
          label={expanded ? 'Collapse payload' : 'Expand payload'}
        >
          <IconButton
            variant="hidden"
            size="xs"
            className="flex-none text-secondary"
            onClick={() => setExpanded(!expanded)}
            aria-label={expanded ? 'Collapse variants' : 'Expand variants'}
            icon={expanded ? <IconChevronDown /> : <IconChevronRight />}
          />
        </Tooltip>
      </div>
      <div>
        {expanded && (
          <JavascriptCodeBlock
            style={{
              margin: 0,
              padding: 0,
              fontFamily: 'var(--font-mono)',
              fontSize: '11px',
            }}
            code={code}
          />
        )}
      </div>
    </div>
  );
};
function updateEvents(
  stateEvents: GraphQLTypes.ProposalLifecycleEvent[] = [],
  eventToToggle: GraphQLTypes.ProposalLifecycleEvent,
): GraphQLTypes.ProposalLifecycleEvent[] {
  return stateEvents.includes(eventToToggle)
    ? stateEvents.filter((event) => event !== eventToToggle)
    : [...stateEvents, eventToToggle];
}

export enum AddEventsMode {
  EDIT = 'EDIT',
  ADD = 'ADD',
}

export function AddEventsModal({
  isOpen,
  state,
  onCheckboxChange,
  onPrevious,
  onComplete,
  onClose,
  mode = AddEventsMode.ADD,
  loading = false,
}: Props) {
  return (
    <Modal isOpen={isOpen} size="lg" onClose={onClose}>
      <ModalOverlay />
      <Form
        onSubmit={() => {
          onComplete();
        }}
        noValidate
      >
        <ModalContent>
          <ModalHeader>
            <ModalTitle>
              {mode === AddEventsMode.ADD ? 'Add' : 'Edit'} events
            </ModalTitle>
            <ModalDescription>
              Please specify which Schema Proposals lifecycle events you would
              like to receive notifications on.
            </ModalDescription>
          </ModalHeader>
          <ModalBody>
            {!loading ? (
              <div className="flex flex-col gap-4">
                <AddEventCheckbox
                  testId="apollo_proposal_created_checkbox"
                  title="Sent when a new proposal is created on this graph."
                  name="APOLLO_PROPOSAL_CREATED"
                  isChecked={state?.events?.includes(
                    GraphQLTypes.ProposalLifecycleEvent.PROPOSAL_CREATED,
                  )}
                  onChange={() =>
                    onCheckboxChange(
                      updateEvents(
                        state?.events,
                        GraphQLTypes.ProposalLifecycleEvent.PROPOSAL_CREATED,
                      ),
                    )
                  }
                  code={proposalCreatedPayload}
                />
                <AddEventCheckbox
                  testId="apollo_proposal_revision_saved_checkbox"
                  title="Sent when a new revision is saved on any proposal on this graph."
                  name="APOLLO_PROPOSAL_REVISION_SAVED"
                  isChecked={state?.events?.includes(
                    GraphQLTypes.ProposalLifecycleEvent.REVISION_SAVED,
                  )}
                  onChange={() =>
                    onCheckboxChange(
                      updateEvents(
                        state?.events,
                        GraphQLTypes.ProposalLifecycleEvent.REVISION_SAVED,
                      ),
                    )
                  }
                  code={revisionSavedPayload}
                />
                <AddEventCheckbox
                  testId="apollo_proposal_status_change_checkbox"
                  title="Sent when any proposal on this graph’s state changes, for example from OPEN to APPROVED."
                  name="APOLLO_PROPOSAL_STATUS_CHANGE"
                  isChecked={state?.events?.includes(
                    GraphQLTypes.ProposalLifecycleEvent.STATUS_CHANGE,
                  )}
                  onChange={() =>
                    onCheckboxChange(
                      updateEvents(
                        state?.events,
                        GraphQLTypes.ProposalLifecycleEvent.STATUS_CHANGE,
                      ),
                    )
                  }
                  code={statusChangePayload}
                />
              </div>
            ) : (
              <div className="flex h-full items-center justify-center">
                <Spinner />
              </div>
            )}
          </ModalBody>
          {!loading && (
            <ModalFooter>
              <Button variant="secondary" type="button" onClick={onClose}>
                Cancel
              </Button>
              <ButtonGroup>
                {onPrevious && (
                  <Button
                    variant="secondary"
                    type="button"
                    onClick={onPrevious}
                  >
                    Previous
                  </Button>
                )}
                <Button
                  variant="primary"
                  type="submit"
                  isDisabled={state?.events?.length === 0}
                >
                  {mode === AddEventsMode.ADD ? 'Next' : 'Save'}
                </Button>
              </ButtonGroup>
            </ModalFooter>
          )}
        </ModalContent>
      </Form>
    </Modal>
  );
}
